import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

// Hook to automatically clear server-side admin cache on page reload
export function useAdminCacheManager() {
  const { data: user } = useQuery({
    queryKey: ['/api/auth/user'],
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  useEffect(() => {
    // Only run for admin users
    if (!user || user.role !== 'admin') {
      return;
    }

    const clearServerCache = async () => {
      try {
        // Check if this is a fresh page load (not just navigation)
        const pageLoadTime = sessionStorage.getItem('admin-page-load-time');
        const currentTime = Date.now().toString();
        
        if (!pageLoadTime) {
          // Fresh page load - clear server-side admin cache
          console.log('[ADMIN CACHE] Fresh admin page load, clearing server cache...');
          
          const response = await fetch('/api/admin/clear-cache', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });

          if (response.ok) {
            console.log('[ADMIN CACHE] Server cache cleared successfully');
          } else {
            console.warn('[ADMIN CACHE] Failed to clear server cache:', response.status);
          }
          
          // Mark this session as having cleared cache
          sessionStorage.setItem('admin-page-load-time', currentTime);
        }
      } catch (error) {
        console.error('[ADMIN CACHE] Error clearing server cache:', error);
      }
    };

    // Run cache clearing on component mount
    clearServerCache();
  }, [user]);

  // Return a manual cache clear function for use in components
  const clearAdminCache = async () => {
    try {
      const response = await fetch('/api/admin/clear-cache', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        console.log('[ADMIN CACHE] Manual server cache clear successful');
        // Also clear the session marker to trigger cache clear on next reload
        sessionStorage.removeItem('admin-page-load-time');
        return true;
      } else {
        console.warn('[ADMIN CACHE] Failed to manually clear server cache:', response.status);
        return false;
      }
    } catch (error) {
      console.error('[ADMIN CACHE] Error manually clearing server cache:', error);
      return false;
    }
  };

  return { clearAdminCache };
}